import React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Grid,
  Autocomplete,
  TextField,
} from "@mui/material";
import { FlexRow, Spacer } from "../../../components";
import { Button as Btn } from "../../../components/atoms/Button";
import { useSnackbar } from "../../../context/SnackbarContext";
import theme from "../../../theme";
import type { ColorsCodeType } from "../../../typings";
import { SimpleCalendar } from "../actions/calendar";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import api from "../../../api/axiosInstance";
import { FiClock } from "react-icons/fi";

interface Interviewer {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  role: string;
  profile_picture_url: string;
}

interface ScheduleInterviewModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

interface FormValues {
  interviewer_id: number | "";
  interview_date: Date | null;
  interview_time_start: string;
  interview_time_end: string;
}

const validationSchema = Yup.object({
  interviewer_id: Yup.number().required("Please select an interviewer"),
  interview_date: Yup.date()
    .required("Please select a date")
    .min(new Date(), "Cannot select a past date"),
  interview_time_start: Yup.string().required("Please select a start time"),
  interview_time_end: Yup.string()
    .required("Please select an end time")
    .test("is-greater", "End time must be after start time", function (value) {
      const { interview_time_start } = this.parent;
      if (!interview_time_start || !value) return true;
      return interview_time_start < value;
    }),
});

export default function ScheduleInterviewModal({
  open,
  onClose,
  scholarshipApplicationId,
  onRefresh,
}: ScheduleInterviewModalProps) {
  const { showSnackbar } = useSnackbar();
  const [interviewers, setInterviewers] = useState<Interviewer[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingInterviewers, setFetchingInterviewers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interviewerSearch, setInterviewerSearch] = useState("");

  const initialValues: FormValues = {
    interviewer_id: "",
    interview_date: null,
    interview_time_start: "",
    interview_time_end: "",
  };

  useEffect(() => {
    if (open) {
      fetchInterviewers();
    }
  }, [open]);

  const fetchInterviewers = async () => {
    setFetchingInterviewers(true);
    setError(null);

    try {
      const response = await api.get("/users?role=INTERVIEWER");
      setInterviewers(response.data.data);
    } catch (err) {
      console.error("Failed to fetch interviewers:", err);
      showSnackbar("Failed to load interviewers. Please try again.", "error");
    } finally {
      setFetchingInterviewers(false);
    }
  };

  const handleScheduleInterview = async (values: FormValues) => {
    if (!values.interview_date) return;

    setLoading(true);
    setError(null);

    try {
      const formattedDate = format(values.interview_date, "yyyy-MM-dd");

      const payload = {
        scholarship_application_id: scholarshipApplicationId,
        interviewer_id: values.interviewer_id as number,
        interview_date: formattedDate,
        interview_time_start: values.interview_time_start,
        interview_time_end: values.interview_time_end,
      };

      const response = await api.post(
        "/scholarship-applications/schedule-interview/",
        payload
      );

      showSnackbar("Interview scheduled successfully!", "success");
      
      // Refresh the applications list
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Failed to schedule interview:", err);
      setError("Failed to schedule interview. Please try again.");
      showSnackbar("Failed to schedule interview. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };



  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 30]) {
        slots.push(
          `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`
        );
      }
    }
    return slots;
  };

  const formatTimeDisplay = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: "6px",
          maxWidth: "468px",
          width: "100%",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "24px 24px 0",
          fontSize: "24px",
          fontWeight: 600,
          color: theme.palette.colors.black[800] as string,
        }}
      >
        Schedule Interview
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleScheduleInterview}
      >
        {({ values, setFieldValue, errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent style={{ padding: "16px 24px" }}>
              <Typography
                variant="body2"
                style={{
                  marginBottom: "16px",
                  color: theme.palette.colors.black[600] as string,
                }}
              >
                Select an interviewer and a date for the scholarship interview.
              </Typography>

              <FormControl
                fullWidth
                error={touched.interviewer_id && Boolean(errors.interviewer_id)}
                style={{ marginBottom: "15px" }}
              >
                <Autocomplete
                  options={interviewers}
                  getOptionLabel={(option) => option.name}
                  value={
                    interviewers.find(
                      (interviewer) => interviewer.id === values.interviewer_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue(
                      "interviewer_id",
                      newValue ? newValue.id : ""
                    );
                  }}
                  loading={fetchingInterviewers}
                  disabled={fetchingInterviewers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Interviewer"
                      error={
                        touched.interviewer_id && Boolean(errors.interviewer_id)
                      }
                      helperText={
                        touched.interviewer_id && errors.interviewer_id
                          ? (errors.interviewer_id as string)
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {fetchingInterviewers ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                      overflow: "auto",
                    },
                  }}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                  noOptionsText={
                    fetchingInterviewers
                      ? "Loading..."
                      : "No interviewers found"
                  }
                />
              </FormControl>

              <Typography
                variant="subtitle2"
                style={{
                  marginBottom: "5px",
                  color: theme.palette.colors.black[700] as string,
                }}
              >
                Interview Date
              </Typography>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "8px 0 16px",
                }}
              >
                <SimpleCalendar
                  selectedDate={values.interview_date}
                  onChange={(date) => setFieldValue("interview_date", date)}
                  disablePastDates={true}
                />

                {touched.interview_date && errors.interview_date && (
                  <Typography
                    color="error"
                    variant="caption"
                    style={{ marginTop: "8px" }}
                  >
                    {errors.interview_date as string}
                  </Typography>
                )}
              </div>
              <Typography
                variant="subtitle2"
                style={{
                  marginTop: "16px",
                  marginBottom: "5px",
                  color: theme.palette.colors.black[700] as string,
                }}
              >
                Interview Time
              </Typography>

              <Grid container spacing={2} style={{ marginBottom: "16px" }}>
                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    error={
                      touched.interview_time_start &&
                      Boolean(errors.interview_time_start)
                    }
                  >
                    <InputLabel id="start-time-label">Start Time</InputLabel>
                    <Select
                      labelId="start-time-label"
                      id="interview_time_start"
                      value={values.interview_time_start}
                      label="Start Time"
                      onChange={(e) => {
                        setFieldValue("interview_time_start", e.target.value);
                        // If end time is not set or is earlier than start time, set it to 30 minutes after start time
                        if (
                          !values.interview_time_end ||
                          values.interview_time_end <= e.target.value
                        ) {
                          const startHour = Number.parseInt(
                            e.target.value.split(":")[0]
                          );
                          const startMinute = Number.parseInt(
                            e.target.value.split(":")[1]
                          );
                          let endHour = startHour;
                          let endMinute = startMinute + 30;

                          if (endMinute >= 60) {
                            endHour = (endHour + 1) % 24;
                            endMinute = endMinute - 60;
                          }

                          const endTime = `${endHour
                            .toString()
                            .padStart(2, "0")}:${endMinute
                            .toString()
                            .padStart(2, "0")}`;
                          setFieldValue("interview_time_end", endTime);
                        }
                      }}
                      startAdornment={
                        <FiClock
                          style={{
                            marginRight: 8,
                            color: theme.palette.colors.black[500] as string,
                          }}
                        />
                      }
                      sx={{
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      {generateTimeSlots().map((time) => (
                        <MenuItem key={time} value={time}>
                          {formatTimeDisplay(time)}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.interview_time_start &&
                      errors.interview_time_start && (
                        <FormHelperText>
                          {errors.interview_time_start as string}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    error={
                      touched.interview_time_end &&
                      Boolean(errors.interview_time_end)
                    }
                  >
                    <InputLabel id="end-time-label">End Time</InputLabel>
                    <Select
                      labelId="end-time-label"
                      id="interview_time_end"
                      value={values.interview_time_end}
                      label="End Time"
                      onChange={(e) =>
                        setFieldValue("interview_time_end", e.target.value)
                      }
                      startAdornment={
                        <FiClock
                          style={{
                            marginRight: 8,
                            color: theme.palette.colors.black[500] as string,
                          }}
                        />
                      }
                      sx={{
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      {generateTimeSlots().map((time) => (
                        <MenuItem
                          key={time}
                          value={time}
                          disabled={time <= values.interview_time_start}
                        >
                          {formatTimeDisplay(time)}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.interview_time_end &&
                      errors.interview_time_end && (
                        <FormHelperText>
                          {errors.interview_time_end as string}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>

            <FlexRow
              justify="flex-end"
              align="center"
              style={{ padding: "5px 24px 16px" }}
            >
              <Btn
                buttonColor={theme.palette.colors.black[600] as ColorsCodeType}
                textColor={theme.palette.colors.white[400] as ColorsCodeType}
                onClick={onClose}
                type="button"
                disabled={loading}
              >
                Cancel
              </Btn>
              <Spacer horizontal={1} />
              <Btn
                type="submit"
                disabled={!(isValid && dirty) || loading}
                buttonColor={theme.palette.colors.green[700] as ColorsCodeType}
                textColor={theme.palette.colors.white[400] as ColorsCodeType}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{
                        marginRight: 8,
                        color: theme.palette.colors.white[400] as string,
                      }}
                    />
                    Scheduling...
                  </>
                ) : (
                  "Schedule"
                )}
              </Btn>
            </FlexRow>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
